/**
 * The session-logging form — the most friction-sensitive surface in the
 * product (trackers die from data-entry chores). So: four required fields up
 * top, quick-pick chips to kill typing, and everything else collapsed. Net is
 * shown live as the user types so logging feels like instant feedback.
 *
 * Presentational: owns field state, calls `onSubmit(LogSessionInput)`. The
 * parent owns the mutation + submitting/error state.
 */

import { useMemo, useState } from "react";

import type { GameType, LogSessionInput } from "@/shared/models";
import { Button } from "@/shared/components/Button";
import { ChevronDown } from "@/shared/components/icons";
import { money } from "@/features/tracker/lib/trackerFormat";

const STAKE_PRESETS = ["1/2", "1/3", "2/5", "5/10"];
const TAG_PRESETS = ["Ran good", "Ran bad", "Tilted", "Focused", "Deep stacked"];

function localDatetimeValue(d: Date): string {
  // datetime-local wants "YYYY-MM-DDTHH:mm" in LOCAL time.
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

const inputClass =
  "w-full rounded-lg border border-border bg-bg px-3 py-2 text-text placeholder:text-text-muted/60 focus:outline-none focus:border-accent/50 transition";

export interface SessionFormProps {
  initial?: Partial<LogSessionInput>;
  submitting?: boolean;
  error?: string | null;
  submitLabel?: string;
  /** Stakes the user has logged before — merged with the presets as quick-pick
   *  chips so a custom stake is one tap next time. */
  stakeOptions?: string[];
  /** Locations the user has logged before — quick-pick chips. */
  locationOptions?: string[];
  onSubmit: (input: LogSessionInput) => void;
  onCancel?: () => void;
}

export function SessionForm({
  initial,
  submitting = false,
  error,
  submitLabel = "Log session",
  stakeOptions = [],
  locationOptions = [],
  onSubmit,
  onCancel,
}: SessionFormProps) {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [gameType, setGameType] = useState<GameType>(initial?.gameType ?? "cash");
  const [stakes, setStakes] = useState(initial?.stakes ?? "");
  const [buyIn, setBuyIn] = useState(initial?.buyIn?.toString() ?? "");
  const [cashOut, setCashOut] = useState(initial?.cashOut?.toString() ?? "");
  const [hours, setHours] = useState(
    initial?.durationMinutes ? (initial.durationMinutes / 60).toString() : "",
  );
  const [startedAt, setStartedAt] = useState(
    localDatetimeValue(initial?.startedAt ? new Date(initial.startedAt) : new Date()),
  );
  const [location, setLocation] = useState(initial?.location ?? "");
  const [tags, setTags] = useState<string[]>(initial?.tags ?? []);
  const [expanded, setExpanded] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const net = useMemo(() => {
    const b = parseFloat(buyIn);
    const c = parseFloat(cashOut);
    if (Number.isNaN(b) || Number.isNaN(c)) return null;
    return c - b;
  }, [buyIn, cashOut]);

  // Presets first, then the user's own custom stakes (so a stake they typed
  // once is a one-tap chip next time).
  const stakeChips = useMemo(
    () => [...STAKE_PRESETS, ...stakeOptions.filter((s) => !STAKE_PRESETS.includes(s))],
    [stakeOptions],
  );

  function toggleTag(tag: string) {
    setTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  }

  function handleSubmit() {
    setLocalError(null);
    const b = parseFloat(buyIn);
    const c = parseFloat(cashOut);
    const h = parseFloat(hours);
    if (gameType === "cash" && !stakes.trim()) {
      setLocalError("What stakes did you play?");
      return;
    }
    if (Number.isNaN(b) || Number.isNaN(c)) {
      setLocalError("Enter your buy-in and cash-out.");
      return;
    }
    if (Number.isNaN(h) || h <= 0) {
      setLocalError("How long did you play?");
      return;
    }
    onSubmit({
      gameType,
      stakes: stakes.trim() || (gameType === "tournament" ? "Tournament" : ""),
      buyIn: b,
      cashOut: c,
      durationMinutes: Math.round(h * 60),
      startedAt: new Date(startedAt).toISOString(),
      title: title.trim() || null,
      notes: notes.trim() || null,
      location: location.trim() || null,
      tags,
    });
  }

  const shownError = error ?? localError;

  return (
    <div className="space-y-4">
      {/* Title — optional but gives the session an identity */}
      <label className="block">
        <span className="text-sm font-medium text-text">Title</span>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value.slice(0, 80))}
          placeholder="Friday night grind (optional)"
          className={`mt-1.5 ${inputClass}`}
        />
      </label>

      {/* Game type */}
      <div className="flex gap-2">
        {(["cash", "tournament"] as GameType[]).map((gt) => (
          <button
            key={gt}
            type="button"
            onClick={() => setGameType(gt)}
            className={[
              "flex-1 rounded-lg border px-3 py-2 text-sm font-medium capitalize transition",
              gameType === gt
                ? "border-accent/50 bg-accent/15 text-text"
                : "border-border text-text-muted hover:text-text",
            ].join(" ")}
          >
            {gt}
          </button>
        ))}
      </div>

      {/* Stakes */}
      <label className="block">
        <span className="text-sm font-medium text-text">
          {gameType === "cash" ? "Stakes" : "Buy-in level"}
        </span>
        <input
          type="text"
          value={stakes}
          onChange={(e) => setStakes(e.target.value)}
          placeholder={gameType === "cash" ? "1/2" : "$100 MTT"}
          className={`mt-1.5 ${inputClass}`}
        />
        {gameType === "cash" && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {stakeChips.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setStakes(p)}
                className={[
                  "rounded-full border px-2.5 py-1 text-xs transition",
                  stakes === p
                    ? "border-accent/50 bg-accent/15 text-text"
                    : "border-border text-text-muted hover:text-text",
                ].join(" ")}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </label>

      {/* Buy-in / Cash-out */}
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-sm font-medium text-text">Buy-in</span>
          <input
            type="number"
            inputMode="decimal"
            value={buyIn}
            onChange={(e) => setBuyIn(e.target.value)}
            placeholder="200"
            className={`mt-1.5 ${inputClass}`}
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-text">Cash-out</span>
          <input
            type="number"
            inputMode="decimal"
            value={cashOut}
            onChange={(e) => setCashOut(e.target.value)}
            placeholder="350"
            className={`mt-1.5 ${inputClass}`}
          />
        </label>
      </div>

      {/* Net preview + duration */}
      <div className="grid grid-cols-2 gap-3 items-end">
        <label className="block">
          <span className="text-sm font-medium text-text">Hours played</span>
          <input
            type="number"
            inputMode="decimal"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder="4"
            className={`mt-1.5 ${inputClass}`}
          />
        </label>
        <div className="rounded-lg border border-border bg-bg px-3 py-2">
          <div className="text-[11px] uppercase tracking-wider text-text-muted">Net</div>
          <div
            className={`text-lg font-semibold tabular-nums ${
              net === null ? "text-text-muted" : net > 0 ? "text-accent" : net < 0 ? "text-danger" : "text-text"
            }`}
          >
            {net === null ? "—" : money(net)}
          </div>
        </div>
      </div>

      {/* More details (collapsed) */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1.5 text-sm text-text-muted hover:text-text transition"
      >
        <ChevronDown
          size={16}
          strokeWidth={2}
          className={`transition ${expanded ? "rotate-180" : ""}`}
        />
        More details (optional)
      </button>

      {expanded && (
        <div className="space-y-4 border-l-2 border-border pl-3">
          <label className="block">
            <span className="text-sm font-medium text-text">When</span>
            <input
              type="datetime-local"
              value={startedAt}
              onChange={(e) => setStartedAt(e.target.value)}
              className={`mt-1.5 ${inputClass}`}
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-text">Location</span>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Bellagio, home game, online…"
              className={`mt-1.5 ${inputClass}`}
            />
            {locationOptions.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {locationOptions.map((loc) => (
                  <button
                    key={loc}
                    type="button"
                    onClick={() => setLocation(loc)}
                    className={[
                      "rounded-full border px-2.5 py-1 text-xs transition",
                      location === loc
                        ? "border-accent/50 bg-accent/15 text-text"
                        : "border-border text-text-muted hover:text-text",
                    ].join(" ")}
                  >
                    {loc}
                  </button>
                ))}
              </div>
            )}
          </label>
          <label className="block">
            <span className="text-sm font-medium text-text">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 2000))}
              rows={3}
              placeholder="How'd it go? Key hands, who was at the table, how you felt — your coach reads this in the debrief."
              className={`mt-1.5 ${inputClass} resize-none`}
            />
          </label>
          <div>
            <span className="text-sm font-medium text-text">Quick tags</span>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {TAG_PRESETS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggleTag(t)}
                  className={[
                    "rounded-full border px-2.5 py-1 text-xs transition",
                    tags.includes(t)
                      ? "border-accent/50 bg-accent/15 text-text"
                      : "border-border text-text-muted hover:text-text",
                  ].join(" ")}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {shownError && <div className="text-sm text-danger">{shownError}</div>}

      <div className="flex gap-2 pt-1">
        {onCancel && (
          <Button variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        )}
        <Button onClick={handleSubmit} disabled={submitting} className="flex-1">
          {submitting ? "Saving…" : submitLabel}
        </Button>
      </div>
    </div>
  );
}
